import { ApiResponseInvoice, Invoice, fromApiInvoice } from './Invoice';

export interface ApiResponseBill {
  billId: number;
  status: string;
  purchaseOrder: string;
  invoice: ApiResponseInvoice;
  invoiceUploadDeadline: string;
  billCreatedAt: string;
  billUpdatedAt: string;
  startPeriodDate: string;
  finalPeriodDate: string;
  paymentDate: string;
}

export interface Bill {
  id: number;
  status: PaymentStatus;
  purchaseOrder: string;
  invoice: Invoice;
  invoiceUploadDeadline: Date;
  createdAt: Date;
  updatedAt: Date;
  startPeriodDate: Date;
  finalPeriodDate: Date;
  paymentDate: Date;
}

// Date-only strings (YYYY-MM-DD) are parsed as UTC by `new Date`, which shifts
// them to the previous day in negative-offset timezones. Replacing the dashes
// with slashes forces local-time parsing. Full datetime strings (ISO with a
// time component) already carry their own offset, so we parse them as-is.
const parseLocalDate = (dateString: string): Date => {
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);

  return new Date(isDateOnly ? dateString.replace(/-/g, '/') : dateString);
};

export const mapApiToBill = (apiData: ApiResponseBill): Bill => {
  return {
    id: apiData.billId,
    status: apiData.status as PaymentStatus,
    purchaseOrder: apiData.purchaseOrder,
    invoice: fromApiInvoice(apiData.invoice),
    invoiceUploadDeadline: parseLocalDate(apiData.invoiceUploadDeadline),
    createdAt: new Date(apiData.billCreatedAt),
    updatedAt: new Date(apiData.billUpdatedAt),
    startPeriodDate: parseLocalDate(apiData.startPeriodDate),
    finalPeriodDate: parseLocalDate(apiData.finalPeriodDate),
    paymentDate: parseLocalDate(apiData.paymentDate),
  };
};

export enum PaymentStatus {
  INVOICE_PENDING = 'INVOICE_PENDING',
  INVOICE_RESENDING = 'INVOICE_RESENDING',
  INVOICE_AUTO_REJECTED = 'INVOICE_AUTO_REJECTED',
  SENDING_EXPIRED = 'SENDING_EXPIRED',
  INVOICE_APPROVED = 'INVOICE_APPROVED',
  INVOICE_AUTO_APPROVED = 'INVOICE_AUTO_APPROVED',
  SETTLING = 'SETTLING',
  SETTLED = 'SETTLED',
  ERROR = 'ERROR',
  UNKNOWN = 'UNKNOWN',
}

export const INVOICE_SUBMITTABLE_STATUSES: PaymentStatus[] = [
  PaymentStatus.INVOICE_PENDING,
  PaymentStatus.INVOICE_RESENDING,
  PaymentStatus.INVOICE_AUTO_REJECTED,
];

export const canSubmitInvoice = (status?: PaymentStatus | null): boolean =>
  status != null && INVOICE_SUBMITTABLE_STATUSES.includes(status);

export const INVOICE_INFO_CARD_STATUSES: PaymentStatus[] = [
  PaymentStatus.INVOICE_PENDING,
  PaymentStatus.INVOICE_RESENDING,
  PaymentStatus.INVOICE_AUTO_REJECTED,
  PaymentStatus.INVOICE_AUTO_APPROVED,
  PaymentStatus.SENDING_EXPIRED,
];

export const shouldShowInvoiceInfoCard = (
  status?: PaymentStatus | null,
): boolean => status != null && INVOICE_INFO_CARD_STATUSES.includes(status);
